import { Injectable } from '@angular/core';
import { RxAppId } from '@shared/models/consts';
import { LoggerService } from '@core/services/logger/logger.service';
import { IteroEventBus } from '@itero/itero-client-communication';
import { IteroBaseEvent } from '@bootstrap/models/base-event.model';
import { v4 as uuid } from 'uuid';
import type { Maybe } from '@/types/globals';

export type EventListener = (event: IteroBaseEvent) => void;

@Injectable({ providedIn: 'root' })
export class EventBusService {
	readonly eventBus: IteroEventBus;

	constructor(private logger: LoggerService) {
		this.eventBus = new IteroEventBus();
	}

	subscribe(listener: Maybe<EventListener>) {
		if (!listener) {
			throw new Error('Event bus listener must be specified.');
		}

		return this.eventBus.subscribe({ channelName: '*', callbackAppId: RxAppId, callbackFunc: listener });
	}

	unsubscribe() {
		this.eventBus.unsubscribe('*', RxAppId);
	}

	async publish<T>(
		eventName: T extends { eventHeader: { eventName: string } } ? T['eventHeader']['eventName'] : string,
		targetApplications: Array<string>,
		eventPayload?: T extends { data: unknown } ? T['data'] : never
	) {
		// Remove when RxApp will be launched in MIDC and MIFL via SL.
		// The values aren't used by the subscribers but event bus requires them to be set.
		// If they are not set, the event bus will throw an error.
		// event bus takes these values from init-context event under the hood.
		if (!this.eventBus.getRegion()) {
			this.eventBus.setRegion('us-west-1');
		}
		if (!this.eventBus.getSessionId()) {
			this.eventBus.setSessionId(uuid());
		}

		const result = await this.eventBus.send({
			publisherAppId: RxAppId,
			eventName,
			targetApplications,
			eventPayload
		});

		if (!result?.success) {
			const message = result?.error?.message ?? `Rx-App: Something went wrong while publishing event ${eventName}`;

			this.logger.error(message, { module: EventBusService.name, error: result?.error });

			if (result?.error) {
				throw result.error;
			}
		}

		return result?.success ?? false;
	}
}
